import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from '../../../hooks';
import { shortNumberForm } from '../../../utils/numbers';
import { resetProfit } from '../../../redux/work';
import styles from "./Balance.module.scss";
import { MakedProfitNotify } from './MakedProfitNotify';
import { setUserBalanceAndLog } from '../../../redux/user/set-user-balance-and-log.handler';

const WORK_TIMER_SETS = {
    duration: 500,
    interval: 10
}

const getWorkProfitItersCount = WORK_TIMER_SETS.duration / WORK_TIMER_SETS.interval;
const workTimerRatio = WORK_TIMER_SETS.interval / WORK_TIMER_SETS.duration;

export const Balance = () => {
    const [workIters, setWorkIters] = useState(getWorkProfitItersCount);
    /** Локальный счётчик анимации; не пишем в Redux на каждом кадре — иначе ломается баланс после интерцептора и дёргаются модалки */
    const [overlayBalance, setOverlayBalance] = useState<number | null>(null);

    const workIntervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const startValueRef = useRef(0);
    const dispatch = useDispatch();

    const { balance } = useSelector(store => store.user.info);
    const workProfit = useSelector(store => store.work.profit);
    const balanceAnimationBase = useSelector(store => store.work.balanceAnimationBase);
    const balanceAnimationTarget = useSelector(store => store.work.balanceAnimationTarget);

    useEffect(() => {
        if (workIntervalIdRef.current === null)
            setWorkIters(getWorkProfitItersCount);
    }, []);

    const releaseWorkTimer = useCallback(() => {
        if (!workIntervalIdRef.current)
            return;

        clearInterval(workIntervalIdRef.current);
        workIntervalIdRef.current = null;
        setWorkIters(getWorkProfitItersCount);
        setOverlayBalance(null);

        dispatch(resetProfit());
    }, [dispatch]);

    useEffect(() => {
        if (!workProfit || workIters <= 0)
            return;

        const tick = () => setWorkIters(prev => prev - 1);

        if (workIntervalIdRef.current)
            return;

        const start = balanceAnimationBase != null ? balanceAnimationBase : balance;
        startValueRef.current = start;
        setOverlayBalance(start);
        workIntervalIdRef.current = setInterval(tick, WORK_TIMER_SETS.interval);
    }, [workProfit, workIters, balanceAnimationBase, balance]);

    useEffect(() => {
        if (!workProfit || !workIntervalIdRef.current)
            return;

        if (workIters > 0) {
            const delta = workTimerRatio * workProfit;
            const deltaBalance = (getWorkProfitItersCount - workIters) * delta;
            setOverlayBalance(startValueRef.current + deltaBalance);
            return;
        }

        const finalBalance =
            balanceAnimationTarget != null
                ? Math.round(balanceAnimationTarget)
                : Math.floor(startValueRef.current + workProfit);
        dispatch(setUserBalanceAndLog(finalBalance));
        releaseWorkTimer();
    }, [workIters, workProfit, balanceAnimationTarget, dispatch, releaseWorkTimer]);

    const shownBalance = overlayBalance != null ? overlayBalance : balance;

    return (
        <>
            <span className={styles.balance}>
                { shortNumberForm(Math.round(shownBalance), 1) }
            </span>

            <MakedProfitNotify />
        </>
    );
};
